import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, CornerDownRight, ArrowRight, Workflow, AlertCircle } from "lucide-react";
import Seo from "../components/Seo";
import DecisionTreeDownload from "../components/DecisionTreeDownload";
import {
  DT_INTRO, DT_DISCLAIMER, DT_CALLOUT, DT_APPLIES_TO, DT_NODES, DT_OUTCOMES,
  DT_PRIORITY_QUESTIONS, DT_DAILY_FILTER, DT_ENV_FILTER, DT_FINAL_RULE,
  DT_OUTCOME_COLOR,
} from "../data/decisionTree";

const OUTCOME_TITLE = Object.fromEntries(DT_OUTCOMES.map((o) => [o.key, o.title]));

function DownloadButton({ testid, className = "" }) {
  return (
    <DecisionTreeDownload
      testid={testid}
      className={`inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 font-mono text-[11px] uppercase tracking-widest font-medium hover:bg-primary-hover transition-colors shadow-[0_0_20px_rgba(0,229,153,0.3)] ${className}`}
    />
  );
}

function Branch({ branch }) {
  const isExit = !!branch.outcome;
  if (isExit) {
    const c = DT_OUTCOME_COLOR[branch.outcome];
    return (
      <div className="flex items-start gap-3">
        <CornerDownRight className="h-4 w-4 shrink-0 mt-2 text-muted-foreground" />
        <div className="flex-1 border-l-2 pl-4 py-1.5" style={{ borderColor: c }}>
          <p className="text-sm text-foreground/80 leading-relaxed">{branch.label}</p>
          <span
            className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm"
            style={{ color: c, borderColor: `${c}66`, backgroundColor: `${c}14` }}
          >
            Outcome {branch.outcome} · {OUTCOME_TITLE[branch.outcome]}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 text-primary">
      <ArrowDown className="h-4 w-4 shrink-0" />
      <p className="font-mono text-[11px] uppercase tracking-widest">{branch.label}</p>
    </div>
  );
}

function DecisionNode({ node, index, isLast }) {
  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.35 }}
        data-testid={`decision-node-${index + 1}`}
        className="relative border border-border bg-surface"
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-primary/50 bg-base font-mono text-xs text-primary">
              {String(index + 1).padStart(2, "0")}
            </span>
            <h3 className="font-display text-lg sm:text-xl font-medium leading-snug pt-0.5">{node.q}</h3>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="border border-border/70 bg-base/40 p-4">
              <p className="mono-label mb-2 text-foreground/50">If No</p>
              <Branch branch={node.no} />
            </div>
            <div className="border border-border/70 bg-base/40 p-4">
              <p className="mono-label mb-2 text-foreground/50">If Yes</p>
              <Branch branch={node.yes} />
            </div>
          </div>
        </div>
      </motion.div>
      {!isLast && (
        <div className="flex justify-center py-3" aria-hidden>
          <span className="h-6 w-px bg-primary/40" />
          <ArrowDown className="h-4 w-4 text-primary/60 -ml-2 mt-4 absolute" />
        </div>
      )}
    </div>
  );
}

function OutcomeCard({ o }) {
  const c = DT_OUTCOME_COLOR[o.key];
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35 }}
      data-testid={`outcome-${o.key.toLowerCase()}`}
      className="relative border border-border bg-surface overflow-hidden"
    >
      <span className="absolute top-0 left-0 h-full w-1" style={{ background: c }} />
      <div className="p-6 sm:p-7 pl-7 sm:pl-8">
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 items-center justify-center font-display text-lg font-semibold border"
            style={{ color: c, borderColor: `${c}66`, backgroundColor: `${c}14` }}
          >
            {o.key}
          </span>
          <h3 className="font-display text-xl font-medium">Outcome {o.key}: {o.title}</h3>
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div>
            <p className="mono-label mb-3" style={{ color: c }}>Use when</p>
            <ul className="space-y-2">
              {o.when.map((w, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-foreground/75 leading-relaxed">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full" style={{ background: c }} />{w}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mono-label mb-3 text-foreground/50">Examples</p>
            <ul className="space-y-2">
              {o.examples.map((e, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-foreground/60 leading-relaxed">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-border" />{e}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function QuestionList({ items, accent = "#00E599" }) {
  return (
    <ul className="space-y-2.5">
      {items.map((q, i) => (
        <li key={i} className="flex gap-3 text-sm text-foreground/75 leading-relaxed">
          <span className="font-mono text-[11px] mt-0.5 shrink-0" style={{ color: accent }}>{String(i + 1).padStart(2, "0")}</span>
          {q}
        </li>
      ))}
    </ul>
  );
}

function Chips({ items, color }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it, i) => (
        <span
          key={i}
          className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border rounded-sm"
          style={{ color, borderColor: `${color}55`, backgroundColor: `${color}10` }}
        >
          {it}
        </span>
      ))}
    </div>
  );
}

const SectionHead = ({ eyebrow, title }) => (
  <div className="mb-8">
    <p className="mono-label">{eyebrow}</p>
    <h2 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">{title}</h2>
  </div>
);

export default function DecisionTreePage() {
  return (
    <div data-testid="decision-tree-page">
      <Seo
        title="Cumulative Exposure Decision Tree"
        description="A calm, engineering-based decision tool for evaluating cumulative exposures across the home, body, workplace, vehicle, and daily life. Free downloadable PDF."
        path="/resources/cumulative-exposure-decision-tree"
      />

      {/* HERO */}
      <section className="relative grain border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8 py-20">
          <Link to="/greenpapers" className="inline-flex items-center gap-2 text-foreground/60 hover:text-primary font-mono text-[11px] uppercase tracking-widest transition-colors">
            <span className="inline-flex items-center gap-2"><Workflow className="h-3.5 w-3.5" /> Decision Tool</span>
          </Link>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Cumulative Exposure Decision Tree
          </h1>
          <p className="mt-4 font-display text-xl md:text-2xl font-light text-foreground/85 leading-snug">
            Home, Body, Work, and Daily-Use Inputs
          </p>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-foreground/65 leading-relaxed">{DT_CALLOUT}</p>
          <div className="mt-8">
            <DownloadButton testid="hero-download-btn" />
          </div>
        </div>
      </section>

      {/* INTRO + DISCLAIMER */}
      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <div className="space-y-5">
          {DT_INTRO.split("\n\n").map((p, i) => (
            <p key={i} className="text-base text-foreground/75 leading-relaxed">{p}</p>
          ))}
        </div>
        <div data-testid="dt-disclaimer" className="mt-8 flex gap-3 border border-border bg-surface/50 p-5">
          <AlertCircle className="h-5 w-5 shrink-0 text-primary/70 mt-0.5" />
          <p className="text-sm text-foreground/65 leading-relaxed">{DT_DISCLAIMER}</p>
        </div>
      </section>

      {/* WHAT THIS APPLIES TO */}
      <section className="border-y border-border bg-surface/20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-14">
          <SectionHead eyebrow="What this tool applies to" title="Exposure inputs across everyday life" />
          <Chips items={DT_APPLIES_TO} color="#0A9F70" />
        </div>
      </section>

      {/* VISUAL DECISION TREE */}
      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <SectionHead eyebrow="The decision flow" title="Work through each question in sequence" />
        <div
          role="img"
          aria-label="Cumulative exposure decision tree flowchart. Work through eight questions in sequence. At each step, a 'No' or 'Yes' answer either routes to one of five outcomes (A: Act Now, B: Test/Document/Evaluate First, C: Reduce and Maintain, D: Defer With a Plan, E: Monitor Only) or continues to the next question."
          className="space-y-0"
        >
          {DT_NODES.map((node, i) => (
            <DecisionNode key={i} node={node} index={i} isLast={i === DT_NODES.length - 1} />
          ))}
        </div>
      </section>

      {/* OUTCOME BOXES */}
      <section className="border-t border-border bg-surface/20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-16">
          <SectionHead eyebrow="Five outcomes" title="Where each path leads" />
          <div className="space-y-6">
            {DT_OUTCOMES.map((o) => <OutcomeCard key={o.key} o={o} />)}
          </div>
        </div>
      </section>

      {/* PRIORITY QUESTIONS + FILTERS */}
      <section className="mx-auto max-w-5xl px-5 sm:px-8 py-16 space-y-14">
        <div>
          <SectionHead eyebrow="Cumulative exposure" title="Priority questions" />
          <div className="border border-border bg-surface p-6 sm:p-8">
            <QuestionList items={DT_PRIORITY_QUESTIONS} />
          </div>
        </div>

        {/* Daily-Use Product Filter */}
        <div data-testid="daily-filter">
          <SectionHead eyebrow="Daily-use product filter" title="Cosmetics, personal-care, cleaning & laundry" />
          <div className="border border-border bg-surface p-6 sm:p-8">
            <p className="text-sm text-foreground/70 leading-relaxed mb-5">{DT_DAILY_FILTER.intro}</p>
            <QuestionList items={DT_DAILY_FILTER.questions} />
            <div className="mt-7 grid gap-6 sm:grid-cols-2">
              <div>
                <p className="mono-label mb-3 text-primary">Higher priority</p>
                <Chips items={DT_DAILY_FILTER.higher} color="#00E599" />
              </div>
              <div>
                <p className="mono-label mb-3 text-foreground/50">Lower priority</p>
                <Chips items={DT_DAILY_FILTER.lower} color="#8A9A93" />
              </div>
            </div>
          </div>
        </div>

        {/* Environment Filter */}
        <div data-testid="env-filter">
          <SectionHead eyebrow="Environment filter" title="Work, vehicles, gyms, salons & shared spaces" />
          <div className="border border-border bg-surface p-6 sm:p-8">
            <p className="text-sm text-foreground/70 leading-relaxed mb-5">{DT_ENV_FILTER.intro}</p>
            <QuestionList items={DT_ENV_FILTER.questions} accent="#43C6F0" />
            <p className="mono-label mt-7 mb-3 text-foreground/50">Practical responses may include</p>
            <ul className="grid gap-2.5 sm:grid-cols-2">
              {DT_ENV_FILTER.responses.map((r, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-foreground/70 leading-relaxed">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary" />{r}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FINAL RULE */}
      <section className="border-t border-border bg-[#0a241a] text-white">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-16">
          <p className="mono-label text-primary">The final rule</p>
          <h2 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">{DT_FINAL_RULE.intro}</h2>
          <ul className="mt-8 grid gap-2.5 sm:grid-cols-2">
            {DT_FINAL_RULE.items.map((it, i) => (
              <li key={i} className="flex gap-3 text-sm text-white/80 leading-relaxed">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary" />{it}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-base text-white/85 leading-relaxed border-l-2 border-primary pl-5">{DT_FINAL_RULE.closing}</p>
        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-16 text-center">
        <div className="relative border border-border bg-surface/50 p-8 sm:p-12">
          <span aria-hidden className="absolute -top-px -left-px h-3.5 w-3.5 border-t-2 border-l-2 border-primary/60" />
          <span aria-hidden className="absolute -top-px -right-px h-3.5 w-3.5 border-t-2 border-r-2 border-primary/60" />
          <span aria-hidden className="absolute -bottom-px -left-px h-3.5 w-3.5 border-b-2 border-l-2 border-primary/60" />
          <span aria-hidden className="absolute -bottom-px -right-px h-3.5 w-3.5 border-b-2 border-r-2 border-primary/60" />
          <p className="mono-label">Take it with you</p>
          <h2 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">Download the printable decision tool</h2>
          <p className="mt-3 max-w-xl mx-auto text-sm text-foreground/60 leading-relaxed">
            A branded, print-ready PDF with the full decision tree, all five outcomes, both filters, and the final rule. Free companion to The Low-Tox Home Blueprint.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <DownloadButton testid="cta-download-btn" />
            <Link to="/products/low-tox-home-blueprint" data-testid="cta-blueprint-link" className="inline-flex items-center gap-2 border border-border px-6 py-3.5 font-mono text-[11px] uppercase tracking-widest hover:border-primary hover:text-primary transition-colors">
              View the Blueprint <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
